import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { TrendingUp, TrendingDown, History } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmt = (n) => `$${(Math.round((Number(n) || 0) * 100) / 100).toLocaleString("en-US")}`;

const DeltaBadge = ({ pct }) => {
  if (pct === null || pct === undefined) {
    return <span className="text-[10px] text-gray-500">—</span>;
  }
  const up = pct > 0;
  const zero = pct === 0;
  return (
    <span
      className={`text-[10px] font-semibold inline-flex items-center gap-0.5 ${
        zero ? "text-gray-500" : up ? "text-emerald-400" : "text-rose-400"
      }`}
    >
      {!zero && (up ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />)}
      {up && !zero ? "+" : ""}
      {pct.toFixed(1)}%
    </span>
  );
};

/**
 * Panel de rendimiento por chatter sobre las últimas N metas cerradas.
 * Renderiza una matriz chatters × periodos con contribución en $ y delta %
 * vs el periodo inmediatamente anterior.
 */
export default function HistorialMetasPanel() {
  const [limit, setLimit] = useState(6);
  const [data, setData] = useState({ periods: [], chatters: [] });
  const [loading, setLoading] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/model-goals/chatter-performance?limit=${limit}`, {
        withCredentials: true,
      });
      setData(r.data || { periods: [], chatters: [] });
    } catch {
      setData({ periods: [], chatters: [] });
    } finally {
      setLoading(false);
    }
  }, [limit]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const noData = !loading && (!data.periods || data.periods.length === 0);

  return (
    <Card
      data-testid="historial-metas-panel"
      className="bg-[#0a0a0a] border border-purple-500/20 rounded-2xl"
    >
      <CardHeader>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-purple-500 to-pink-600 flex items-center justify-center">
              <History className="w-6 h-6 text-white" />
            </div>
            <div>
              <CardTitle className="text-white">Rendimiento histórico de Chatters</CardTitle>
              <p className="text-xs text-gray-400 mt-0.5">
                Basado en reportes cerrados por mes — muestra cuánto aportó cada chatter y su variación %.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {[3, 6, 12].map((n) => (
              <Button
                key={n}
                data-testid={`historial-limit-${n}`}
                variant={limit === n ? "default" : "ghost"}
                size="sm"
                onClick={() => setLimit(n)}
                className={
                  limit === n
                    ? "bg-purple-500/20 text-purple-200 border border-purple-500/40"
                    : "text-gray-400 hover:text-white"
                }
              >
                Últimas {n}
              </Button>
            ))}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {loading && <div className="text-gray-500 text-sm py-4">Cargando…</div>}
        {noData && (
          <div className="text-gray-500 text-sm py-6 text-center">
            Aún no hay reportes registrados en los últimos meses.
          </div>
        )}
        {!noData && !loading && (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="border-b border-white/10">
                  <th className="text-left text-xs uppercase text-gray-500 py-2 pr-4 font-medium">
                    Chatter
                  </th>
                  {(data.periods || []).map((p) => (
                    <th
                      key={p.period_key || p.goal_id}
                      className="text-right text-xs text-gray-500 py-2 px-3 min-w-[130px]"
                    >
                      <div className="text-purple-300 font-semibold">{p.period_name}</div>
                      <div className="text-[10px] text-gray-500 font-normal">
                        {p.fecha_inicio} → {p.fecha_fin}
                      </div>
                    </th>
                  ))}
                  <th className="text-right text-xs uppercase text-gray-500 py-2 pl-4 font-medium">
                    Total
                  </th>
                </tr>
              </thead>
              <tbody>
                {(data.chatters || []).map((c) => (
                  <tr key={c.chatter_id} className="border-b border-white/5 hover:bg-white/[0.02]">
                    <td className="py-3 pr-4 text-white font-medium">{c.chatter_name}</td>
                    {c.cells.map((cell, idx) => (
                      <td key={idx} className="py-3 px-3 text-right">
                        <div className="text-white">{fmt(cell.amount)}</div>
                        <DeltaBadge pct={cell.delta_pct} />
                      </td>
                    ))}
                    <td className="py-3 pl-4 text-right">
                      <div className="text-emerald-400 font-bold">{fmt(c.total)}</div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
